import { useEffect, useRef, type ReactNode } from "react";
import { scrollParentToBottom } from "@/lib/scrollParent";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { EmptyState, TruncatedText } from "@/components/ui";
import {
  chatLocalDayKey,
  formatChatDaySeparator,
} from "./formatChatDaySeparator";
import { MessengerChatBubble } from "./MessengerChatBubble";
import { MessengerChatInput } from "./MessengerChatInput";
import { MessengerChatRefreshButton } from "./MessengerChatRefreshButton";
import {
  CHAT_CLOSED_FOOTER_CLASS,
  CHAT_HEADER_CLASS,
  CHAT_SHELL_MIN_HEIGHT,
  CHAT_WALLPAPER_CLASS,
} from "./theme";
import type { ChatMessage } from "./types";

function ChatDaySeparator({ label }: { label: string }) {
  return (
    <div className="flex justify-center px-2 py-2" role="separator">
      <span className="rounded-full bg-surface-0 px-2.5 py-0.5 text-[11px] font-medium text-surface-500 shadow-xs ring-1 ring-surface-200">
        {label}
      </span>
    </div>
  );
}

export type MessengerChatFooter =
  | { kind: "composer"; node: ReactNode }
  | {
      kind: "input";
      onSend: (text: string) => Promise<void>;
      isSending?: boolean;
      placeholder?: string;
      sendLabel?: string;
      loadingLabel?: string;
      disabled?: boolean;
    }
  | { kind: "closed"; node: ReactNode };

export interface MessengerChatProps {
  title: string;
  subtitle?: string;
  avatarInitials?: string;
  headerExtra?: ReactNode;
  /** Hide the avatar/title header (outer chrome provides it). */
  hideHeader?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  messages: ChatMessage[];
  emptyText?: string;
  className?: string;
  minHeightClassName?: string;
  blockedLabel?: string;
  footer?: MessengerChatFooter;
}

function initialsFromTitle(title: string): string {
  return title
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function MessengerChat({
  title,
  subtitle,
  avatarInitials,
  headerExtra,
  hideHeader = false,
  onRefresh,
  isRefreshing = false,
  messages,
  emptyText = "No messages yet",
  className,
  minHeightClassName = CHAT_SHELL_MIN_HEIGHT,
  blockedLabel,
  footer,
}: MessengerChatProps) {
  const { t } = useTranslation("common");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initials = avatarInitials ?? initialsFromTitle(title);
  const dayLabels = {
    today: t("chat.dayToday"),
    yesterday: t("chat.dayYesterday"),
  };

  useEffect(() => {
    scrollParentToBottom(messagesEndRef.current);
  }, [messages.length]);

  return (
    <div
      className={cn("flex min-h-0 flex-col", minHeightClassName, className)}
    >
      {!hideHeader ? (
        <div className={CHAT_HEADER_CLASS}>
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[13px] font-semibold text-primary-700 ring-1 ring-inset ring-primary-600/15"
            aria-hidden
          >
            {initials || "?"}
          </div>
          <div className="min-w-0 flex-1">
            <TruncatedText
              as="p"
              className="text-sm font-semibold tracking-tight text-surface-900"
            >
              {title}
            </TruncatedText>
            {subtitle ? (
              <TruncatedText as="p" className="text-xs text-surface-500">
                {subtitle}
              </TruncatedText>
            ) : null}
          </div>
          {onRefresh || headerExtra ? (
            <div className="flex shrink-0 items-center gap-1">
              {onRefresh ? (
                <MessengerChatRefreshButton
                  onRefresh={onRefresh}
                  isRefreshing={isRefreshing}
                />
              ) : null}
              {headerExtra}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={CHAT_WALLPAPER_CLASS}>
        <div className="mx-auto min-w-0 w-full max-w-3xl space-y-0.5">
          {messages.length === 0 ? (
            <EmptyState
              size="sm"
              showIcon={false}
              title={emptyText}
              className="py-10"
            />
          ) : (
            messages.map((message, index) => {
              const dayKey = chatLocalDayKey(message.createdAt);
              const previousDayKey =
                index > 0
                  ? chatLocalDayKey(messages[index - 1].createdAt)
                  : null;
              const showDaySeparator = dayKey !== previousDayKey;

              return (
                <div key={message.id}>
                  {showDaySeparator ? (
                    <ChatDaySeparator
                      label={formatChatDaySeparator(
                        message.createdAt,
                        dayLabels,
                      )}
                    />
                  ) : null}
                  <MessengerChatBubble
                    message={message}
                    blockedLabel={blockedLabel}
                  />
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {footer?.kind === "closed" ? (
        <div className={CHAT_CLOSED_FOOTER_CLASS}>{footer.node}</div>
      ) : null}

      {footer?.kind === "composer" ? footer.node : null}

      {footer?.kind === "input" ? (
        <MessengerChatInput
          onSend={footer.onSend}
          isSending={footer.isSending ?? false}
          disabled={footer.disabled ?? false}
          placeholder={footer.placeholder}
          sendLabel={footer.sendLabel ?? t("send")}
          loadingLabel={footer.loadingLabel ?? t("loading")}
        />
      ) : null}
    </div>
  );
}
